import apiConfig from '../config/apiConfig.js';

const baseUrl = apiConfig.baseUrl;

// Cache for recent requests to avoid duplicate calls
const requestCache = new Map();
const CACHE_DURATION = 5000; // 5 seconds cache

// Helper function to format dates for API
const formatDateForAPI = (dateString) => {
  if (!dateString) return null;
  
  // Ensure the date is in YYYY-MM-DD format
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    console.error('Invalid date:', dateString);
    return null;
  }
  
  // Format as YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

// Helper function to get auth headers
const getAuthHeaders = async (authContext) => {
  const headers = {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  };

  try {
    if (authContext && authContext.getAccessToken) {
      const accessToken = await authContext.getAccessToken();
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
        console.log('Added Authorization header');
      }
    }
  } catch (error) {
    console.error('Failed to get access token:', error);
  }

  return headers;
};

export async function fetchChatLogs({ page = 1, limit = 100, from, to, authContext } = {}) {
  let timeoutId;
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('perPage', limit); // ✅ FIXED HERE
    
    // Format dates properly for API
    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);
    
    if (formattedFrom) {
      params.append('from', formattedFrom);
      console.log('Adding from date:', formattedFrom);
    }
    if (formattedTo) {
      params.append('to', formattedTo);
      console.log('Adding to date:', formattedTo);
    }

    const url = `${baseUrl}/api/chatlogs?${params.toString()}`;
    console.log('Full API URL:', url);

    const cacheKey = url;
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('Returning cached response for:', url);
      return cached.data;
    }

    console.log('Making API request to:', url);

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 10000);

    const headers = await getAuthHeaders(authContext);

    const response = await fetch(url, {
      signal: controller.signal,
      headers
    });

    clearTimeout(timeoutId);
    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      
      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in again.');
      } else if (response.status === 403) {
        throw new Error('Access denied. You do not have permission to view these logs.');
      } else if (response.status === 404) {
        throw new Error('Chat logs not found for the specified criteria.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`Failed to fetch chat logs: ${response.status} ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('API Response data:', {
      total: data.total,
      logsCount: data.logs?.length || 0,
      page,
      limit,
      from: formattedFrom,
      to: formattedTo
    });

    requestCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    return data;
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    console.error('Error fetching chat logs:', error);

    // Clear the correct cache key
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('perPage', limit);
    
    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);
    
    if (formattedFrom) params.append('from', formattedFrom);
    if (formattedTo) params.append('to', formattedTo);

    requestCache.delete(`${baseUrl}/api/chatlogs?${params.toString()}`);

    throw error;
  }
}

export async function fetchChatLogDashboard(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/chatlogs/dashboard`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch dashboard: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching dashboard:', error);
    }
    throw error;
  }
}

export async function fetchChatLogAnalytics(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/chatlogs/analytics`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
  
    clearTimeout(timeout);
   
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch analytics: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching analytics:', error);
    }
    throw error;
  }
}

// ========== TRANSLATE LOGS API FUNCTIONS ==========

export async function fetchTranslateLogs({ page = 1, limit = 100, from, to, authContext } = {}) {
  let timeoutId;
  try {
    // Ensure limit and page are within safe bounds
    const safeLimit = Math.min(Math.max(parseInt(limit) || 100, 1), 1000);
    const safePage = Math.max(parseInt(page) || 1, 1);

    if (limit !== safeLimit) {
      console.warn(`⚠️ Adjusted 'perPage' from ${limit} to safe value: ${safeLimit}`);
    }

    const params = new URLSearchParams();
    params.append('page', safePage);
    params.append('perPage', safeLimit);

    // Format dates properly for API
    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);

    if (formattedFrom) {
      params.append('from', formattedFrom);
      console.log('Adding from date:', formattedFrom);
    }
    if (formattedTo) {
      params.append('to', formattedTo);
      console.log('Adding to date:', formattedTo);
    }

    const url = `${baseUrl}/api/translatelogs?${params.toString()}`;
    console.log('Full Translate API URL:', url);

    const cacheKey = url;
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('Returning cached translate response for:', url);
      return cached.data;
    }

    console.log('Making Translate API request to:', url);

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 10000);

    const headers = await getAuthHeaders(authContext);

    const response = await fetch(url, {
      signal: controller.signal,
      headers
    });

    clearTimeout(timeoutId);
    console.log('Translate Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Translate API Error Response:', errorText);

      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in again.');
      } else if (response.status === 403) {
        throw new Error('Access denied. You do not have permission to view these logs.');
      } else if (response.status === 404) {
        throw new Error('Translate logs not found for the specified criteria.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`Failed to fetch translate logs: ${response.status} ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('Translate API Response data:', {
      total: data.total,
      logsCount: data.logs?.length || 0,
      page: safePage,
      limit: safeLimit,
      from: formattedFrom,
      to: formattedTo
    });

    requestCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    return data;
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    console.error('Error fetching translate logs:', error);

    // Invalidate cache for the specific request
    const safeLimit = Math.min(Math.max(parseInt(limit) || 100, 1), 1000);
    const safePage = Math.max(parseInt(page) || 1, 1);

    const params = new URLSearchParams();
    params.append('page', safePage);
    params.append('perPage', safeLimit);

    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);

    if (formattedFrom) params.append('from', formattedFrom);
    if (formattedTo) params.append('to', formattedTo);

    requestCache.delete(`${baseUrl}/api/translatelogs?${params.toString()}`);

    throw error;
  }
}


export async function fetchTranslateLogDashboard(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/translatelogs/dashboard`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeout);
  
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch translate dashboard: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching translate dashboard:', error);
    }
    throw error;
  }
}

export async function fetchTranslateLogAnalytics(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/translatelogs/analytics`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
  
    clearTimeout(timeout);
   
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch translate analytics: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching translate analytics:', error);
    }
    throw error;
  }
}



// ========== Query LOGS API FUNCTIONS ==========
export async function fetchQueryLogAnalytics(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/querylogs/analytics`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
  
    clearTimeout(timeout);
   
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch query analytics: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching query analytics:', error);
    }
    throw error;
  }
}

export async function fetchQueryLogs({ page = 1, limit = 100, from, to, authContext } = {}) {
  let timeoutId;
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('perPage', limit);
    
    // Format dates properly for API
    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);
    
    if (formattedFrom) {
      params.append('from', formattedFrom);
      console.log('Adding from date:', formattedFrom);
    }
    if (formattedTo) {
      params.append('to', formattedTo);
      console.log('Adding to date:', formattedTo);
    }

    const url = `${baseUrl}/api/querylogs?${params.toString()}`;
    console.log('Full Query API URL:', url);

    const cacheKey = url;
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      console.log('Returning cached response for:', url);
      return cached.data;
    }

    console.log('Making API request to:', url);

    const controller = new AbortController();
    timeoutId = setTimeout(() => controller.abort(), 10000);

    const headers = await getAuthHeaders(authContext);

    const response = await fetch(url, {
      signal: controller.signal,
      headers
    });

    clearTimeout(timeoutId);
    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API Error Response:', errorText);
      
      // Handle specific error cases
      if (response.status === 401) {
        throw new Error('Authentication required. Please sign in again.');
      } else if (response.status === 403) {
        throw new Error('Access denied. You do not have permission to view these logs.');
      } else if (response.status === 404) {
        throw new Error('Query logs not found for the specified criteria.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later.');
      } else {
        throw new Error(`Failed to fetch query logs: ${response.status} ${errorText}`);
      }
    }

    const data = await response.json();
    console.log('Query API Response data:', {
      total: data.total,
      logsCount: data.logs?.length || 0,
      page,
      limit,
      from: formattedFrom,
      to: formattedTo
    });

    requestCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });

    return data;
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    console.error('Error fetching query logs:', error);

    // Clear the correct cache key
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('perPage', limit);
    
    const formattedFrom = formatDateForAPI(from);
    const formattedTo = formatDateForAPI(to);
    
    if (formattedFrom) params.append('from', formattedFrom);
    if (formattedTo) params.append('to', formattedTo);

    requestCache.delete(`${baseUrl}/api/querylogs?${params.toString()}`);

    throw error;
  }
}

export async function fetchQueryLogDashboard(authContext) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 8000);
  try {
    const headers = await getAuthHeaders(authContext);
    
    const response = await fetch(`${baseUrl}/api/querylogs/dashboard`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeout);
  
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch query dashboard: ${errorText}`);
    }
    return await response.json();
  } catch (error) {
    clearTimeout(timeout);
    // Don't log abort errors as they're expected
    if (error.name !== 'AbortError') {
      console.error('Error fetching query dashboard:', error);
    }
    throw error;
  }
}

